import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { PageShell } from "@/components/page-shell";
import { ObfuscatedEmail } from "@/components/obfuscated-email";
import steveAsset from "@/assets/steve.jpg.asset.json";
import oliAsset from "@/assets/oli.jpg.asset.json";
import teoAsset from "@/assets/teo.jpg.asset.json";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About WeVang! — The Squad & Our Philosophy" },
      {
        name: "description",
        content:
          "WeVang! works at the intersection of strategy, product, market, and design. Meet Steve, Oli, and Teo.",
      },
      { property: "og:title", content: "About WeVang! — The Squad & Our Philosophy" },
      {
        property: "og:description",
        content:
          "WeVang! works at the intersection of strategy, product, market, and design. Meet Steve, Oli, and Teo.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://www.wevang.com/about" },
    ],
    links: [{ rel: "canonical", href: "https://www.wevang.com/about" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "About WeVang!",
          url: "https://www.wevang.com/about",
          mainEntity: {
            "@type": "Organization",
            name: "WeVang!",
            url: "https://www.wevang.com",
            logo: "https://www.wevang.com/logo.svg",
            founder: [
              { "@type": "Person", name: "Steve" },
              { "@type": "Person", name: "Oli" },
              { "@type": "Person", name: "Teo" },
            ],
          },
        }),
      },
    ],
  }),
  component: AboutPage,
});

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-4">
      <span className="mt-[0.5rem] h-px w-10 shrink-0 bg-primary" />
      <p className="font-sans text-[14px] font-bold uppercase leading-relaxed tracking-[0.28em] text-primary">
        {children}
      </p>
    </div>
  );
}


function Section({
  children,
  watermark,
  className = "",
}: {
  children: ReactNode;
  watermark?: string;
  className?: string;
}) {
  return (
    <section
      className={`relative isolate overflow-hidden px-6 py-24 md:px-12 md:py-36 ${className}`}
    >
      {watermark && (
        <span
          aria-hidden
          className="watermark pointer-events-none absolute bottom-4 right-2 -z-10 hidden select-none text-[6.5vw] opacity-[0.07] lg:block"
        >
          {watermark}
        </span>
      )}
      <div className="relative mx-auto max-w-[1400px]">
        <div className="max-w-4xl">{children}</div>
      </div>
    </section>
  );
}

function Founder({
  name,
  role,
  bio,
  image,
  imageAlt,
  imagePosition = "center",
  links,
}: {
  name: string;
  role: string;
  bio: string;
  image?: string;
  imageAlt?: string;
  imagePosition?: string;
  links?: { href: string; label: string }[];
}) {
  return (
    <article className="bg-white p-10">
      {image ? (
        <img
          src={image}
          alt={imageAlt ?? `Photo of ${name}`}
          className="aspect-square w-full object-cover"
          style={{ objectPosition: imagePosition }}
        />
      ) : (
        <div
          role="img"
          aria-label={`Photo placeholder — ${name}`}
          className="flex aspect-square w-full items-center justify-center border border-border bg-muted"
        >
          <span className="px-4 text-center font-sans text-[12px] font-normal uppercase tracking-[0.28em] text-foreground/70">
            Photo placeholder — {name}
          </span>
        </div>
      )}
      <h3 className="mt-8 font-display text-[36px] font-extrabold tracking-tighter text-foreground">
        {name}
      </h3>
      <p className="mt-2 font-sans text-[18px] font-bold text-primary">{role}</p>
      <p className="mt-6 font-sans text-[18px] font-normal leading-relaxed text-foreground/60">
        {bio}
      </p>
      {links && links.length > 0 && (
        <div className="mt-8 flex flex-col items-start gap-3 border-t border-border pt-6">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="font-sans text-[75%] font-semibold text-[#1D4ED8] underline decoration-[#1D4ED8]/30 underline-offset-2 transition-colors hover:decoration-[#1D4ED8] hover:text-[#1E3A8A]"
            >
              {link.label}
            </a>
          ))}
        </div>
      )}
    </article>
  );
}

function AboutPage() {
  return (
    <PageShell>
      {/* HERO — dark charcoal fading to white */}
      <section
        className="relative isolate overflow-hidden px-6 pb-32 pt-48 md:px-12 md:pb-44 md:pt-56"
        style={{
          backgroundImage:
            "linear-gradient(to bottom, rgba(26,26,26,1) 0%, rgba(26,26,26,1) 8%, rgba(26,26,26,0.92) 24%, rgba(26,26,26,0.78) 40%, rgba(26,26,26,0.56) 56%, rgba(26,26,26,0.32) 72%, rgba(26,26,26,0.14) 85%, rgba(26,26,26,0.04) 94%, rgba(26,26,26,0) 100%)",
        }}
      >
        <span
          aria-hidden
          className="watermark pointer-events-none absolute -right-4 top-36 z-0 select-none text-[20vw] leading-none opacity-[0.07]"
          style={{ WebkitTextStroke: "1px rgba(255,255,255,0.5)" }}
        >
          About
        </span>
        <div className="relative z-20 mx-auto max-w-[1400px]">
          <Eyebrow>THE ERA OF &lsquo;GOOD ENOUGH&rsquo; IS OVER.</Eyebrow>
          <h1 className="mt-10 max-w-5xl font-display text-[44px] font-extrabold leading-[1.05] tracking-tighter text-white md:text-[72px]">
            We saw visionaries held back by fragmented approaches and decided to{" "}
            <span className="font-extrabold text-primary">forge a new path</span>.
          </h1>
          <p className="mt-10 max-w-3xl font-sans text-[20px] font-light leading-relaxed text-white/70 md:text-[26px]">
            Born from the frustration of diluted strategies and stalled traction, we emerged to
            eliminate the chaos — one where excellence is non-negotiable and every product, message,
            and design delivers{" "}
            <span className="font-semibold text-primary">
              undeniable purpose, power, and precision
            </span>
            .
          </p>
        </div>
      </section>

      {/* WHO ARE WE */}
      <Section watermark="Vanguard">
        <h2 className="font-display text-[56px] font-extrabold leading-[0.95] tracking-tighter text-foreground md:text-[88px]">
          We are WeVang<span className="text-primary">!</span>
        </h2>
        <p className="mt-10 font-sans text-[28px] font-light leading-[1.6] text-foreground md:text-[36px]">
          Not a brand. A statement.
        </p>
        <p className="mt-6 max-w-3xl font-sans text-[20px] font-normal leading-relaxed text-foreground/60 md:text-[24px]">
          A unified drive moving forward together. Your success built on our combined muscle. The
          vanguard leading the charge and setting the pace before anyone else moves. The shockwave
          of disruption. The impact you can&apos;t ignore.
        </p>
      </Section>

      {/* THE NEXT WAVE IS HERE */}
      <div className="surface-light">
        <Section watermark="Next Wave">
          <Eyebrow>THE NEXT WAVE IS HERE</Eyebrow>
          <h2 className="mt-10 font-display text-[34px] font-bold leading-[1.1] tracking-tighter text-foreground md:text-[52px]">
            We are relentlessly innovating, constantly refining our methodologies to anticipate
            market shifts.
          </h2>
          <p className="mt-8 max-w-3xl font-sans text-[20px] font-light leading-relaxed text-foreground/60 md:text-[24px]">
            Our vision extends beyond today&apos;s victories; we are building the frameworks for
            tomorrow&apos;s industry-defining brands, ensuring our clients remain at the absolute
            forefront.
          </p>
        </Section>
      </div>

      {/* HOW WE THINK */}
      <Section watermark="Gaps">
        <Eyebrow>HOW WE THINK</Eyebrow>
        <h2 className="mt-10 font-display text-[32px] font-bold leading-[1.15] tracking-tighter text-foreground md:text-[48px]">
          The hardest problems rarely live inside one function — they live in the gaps between them.
        </h2>
        <div className="mt-10 max-w-3xl space-y-7 font-sans text-[18px] font-normal leading-relaxed text-foreground/60 md:text-[20px]">
          <p>
            WeVang! wasn&apos;t built around a single discipline. It was built around a single idea:
            where a{" "}
            <span className="font-bold">brilliant product fails from weak positioning</span>, or a{" "}
            <span className="font-bold">sharp strategy stalls from disconnected execution</span>.
          </p>
          <p>
            We work at the intersection of strategy, product, market, and design —{" "}
            <span className="font-bold text-primary">
              reducing uncertainty, bringing structure where it&apos;s missing
            </span>
            , connecting the dots across functions that too often operate in isolation.
          </p>
          <p>
            We&apos;re generalists by design, but not loose ones. Our value isn&apos;t in knowing one
            discipline deeper than anyone else. It&apos;s in{" "}
            <span className="font-bold text-primary">
              seeing the whole system clearly enough to know which gap matters most
            </span>{" "}
            right now, and having the judgment to close it. We do our best work close to founders,
            CEOs, and leadership teams — especially in{" "}
            <span className="font-bold">moments of strategic change</span>, where the business, the
            product, the market, and the brand all need to be read together, not as separate
            conversations.
          </p>
        </div>
      </Section>

      {/* TEAM — text only */}
      <Section watermark="Collective">
        <Eyebrow>A COLLECTIVE WORKING AS A SYSTEM</Eyebrow>
        <h2 className="mt-10 font-display text-[30px] font-bold leading-[1.15] tracking-tighter text-foreground md:text-[44px]">
          We are...&nbsp;
        </h2>
        <p className="mt-8 max-w-3xl font-sans text-[20px] font-normal leading-relaxed text-foreground/60 md:text-[24px]">
          Executive leaders and strategists. Lawyers and engineers. Artists, photographers, and
          filmmakers. Graphic designers, digital marketers, musicians, writers, and AI specialists.{" "}
          <span className="font-semibold text-primary">
            Our collective experience is your competitive advantage
          </span>
          , ensuring every decision is informed, every action is precise, and every outcome is
          definitive.
        </p>
      </Section>

      {/* LEADERSHIP */}
      <div className="surface-light border-t border-border">
        <section className="relative isolate overflow-hidden px-6 py-32 md:px-12 md:py-44">
          <span
            aria-hidden
            className="watermark pointer-events-none absolute bottom-4 left-2 -z-10 hidden select-none text-[6.5vw] opacity-[0.07] lg:block"
          >
            The Squad
          </span>
          <div className="relative mx-auto max-w-[1400px]">
            <div className="max-w-4xl">
              <Eyebrow>LEADERSHIP</Eyebrow>
              <h2 className="mt-10 font-display text-[42px] font-extrabold leading-[1.05] tracking-tighter text-foreground md:text-[64px]">
                Three founders.&nbsp;
                <br />
                Three disciplines.&nbsp;
                <br />
                One recommendation.
              </h2>
              <p className="mt-8 font-sans text-[24px] font-light text-foreground/60 md:text-[30px]">
                This is The Squad.
              </p>
            </div>

            <div className="mt-20 grid grid-cols-1 gap-6 md:grid-cols-3">
              <Founder
                name="Steve"
                role="Leads Strategy, Product & Execution."
                bio="Steve is a true architect — blending strategic foresight with a deep understanding of how various disciplines converge to transform brilliant ideas into real-world ventures. He leads strategy, product, and execution as one continuous system — the person who owns the direction when a company faces its hardest decisions: what to build, how to move, where to stake the next bet. He's led that kind of decision before: inside the chaos of turnarounds and pivots across 15+ companies and 9 industries, and inside one venture he took from zero to acquisition. He doesn't advise from the outside. He leads from inside the decision."
                 image={steveAsset.url}
                 imagePosition="50% 50%"
                 imageAlt="Steve, WeVang! co-founder"
                 links={[
                   { href: "https://www.mendewicz.com", label: "www.mendewicz.com" },
                   {
                     href: "https://www.linkedin.com/in/mendewicz",
                     label: "LinkedIn",
                   },
                 ]}
               />
              <Founder
                name="Oli"
                role="Leads Marketing & Growth."
                bio="Oli leads with a simple conviction: a brand that isn't understood is a brand that doesn't exist — no matter how loud the channels are. She owns marketing and growth end to end — strategy, positioning, and the campaigns that carry them into the world. She sets the direction others execute against, deciding not just where a brand shows up, but why it deserves attention there."
                image={oliAsset.url}
                imagePosition="50% 18%"
                imageAlt="Oli, WeVang! co-founder"
              />
              <Founder
                name="Teo"
                role="Leads Design & Creative Direction."
                bio="Teo leads with a simple belief: if it doesn't look inevitable, it isn't finished — every visual decision either builds the brand or dilutes it. He owns creative direction end to end — graphic design, web design, AI-driven design, photography, film, and production. He sets the visual language others build within, deciding not just how something looks, but what it says before a single word is read. Share the vision. It comes back through his lens as something defined, sharp, unmistakably yours."
                image={teoAsset.url}
                imagePosition="50% 20%"
                imageAlt="Teo, WeVang! co-founder"
              />
            </div>
          </div>
        </section>
      </div>

      {/* CTA */}
      <section className="px-6 pb-40 pt-24 text-center md:px-12 md:pb-52">
        <ObfuscatedEmail className="text-sm tracking-[0.3em] text-foreground/70 transition-colors hover:text-primary" />
      </section>
    </PageShell>
  );
}
